import dayjs, { Dayjs } from 'dayjs';

import {
  BookingModeDto,
  DictionaryTypeCalendarConfigurationDto,
  DictionaryTypeDto,
  ProcessCalendarEntryQueryDto,
  OccupancyModeDto,
} from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import {
  BUSINESS_TIME_ZONE,
  businessDay,
  DATE_FORMAT,
  nextBusinessDay,
  reservationRangeFromDto,
  reservationRangeToRequest,
  TIME_FORMAT,
} from '@/utils/reservationRange.util.ts';
import {
  ProcessCalendarEntryQueryFormModel,
  ProcessCalendarEntryQueryFormRange,
  ProcessCalendarEntryQueryIsoRange,
  ProcessCalendarEntryQueryValues,
} from '@/views/processes/calendarEntryQuery/model/ProcessCalendarEntryQueryForm.model.ts';

/** ISO instant with milliseconds; unlike toISOString it keeps an invalid (partly typed) date readable. */
const INSTANT_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export type CalendarEntryQuerySummaryTranslations = {
  resourceWithRange: (resource: string, range: string) => string;
  typeWithEntry: (type: string, entry: string) => string;
  withoutRange: string;
};

export type ProcessNameTranslations = {
  entryWithoutRange: (entry: string) => string;
  entryWithRange: (entry: string, range: string) => string;
  rangeWithoutEntry: (range: string) => string;
};

type NameContext = {
  bookingMode: BookingModeDto;
  entryName?: string;
  from?: Dayjs | null;
  to?: Dayjs | null;
};

type SummaryContext = {
  entry?: string;
  /** Already formatted stored range. */
  range?: string;
  /** Label of the dictionary type. */
  type?: string;
};

/** The entry name is read-only: it follows the entry id and does not make a query on its own. */
const QUERY_FIELDS: readonly (keyof ProcessCalendarEntryQueryDto)[] = [
  'requestedDictionaryType',
  'requestedDictionaryId',
  'requestedFrom',
  'requestedTo',
];

const inBusinessZone = (value: Dayjs | string): Dayjs => dayjs(value).tz(BUSINESS_TIME_ZONE);

/** Like reservations; the form range is read in the business zone first. */
const toRequestRange = (from: Dayjs, to: Dayjs, bookingMode: BookingModeDto) =>
  reservationRangeToRequest(bookingMode, inBusinessZone(from), inBusinessZone(to));

export const processCalendarEntryQueryUtil = {
  /** Booking mode of a type; without enabled calendars (or configuration) the query is full-day. */
  bookingMode: (configuration?: DictionaryTypeCalendarConfigurationDto): BookingModeDto =>
    configuration?.calendarsEnabled && configuration.bookingMode === BookingModeDto.ExactTime
      ? BookingModeDto.ExactTime
      : BookingModeDto.FullDay,

  buildProcessName: (context: NameContext, translations: ProcessNameTranslations): string => {
    const { bookingMode, entryName, from, to } = context;
    const requestRange = from && to ? toRequestRange(from, to, bookingMode) : undefined;
    const range = requestRange
      ? processCalendarEntryQueryUtil.formatRange(requestRange.from, requestRange.to, bookingMode)
      : undefined;
    if (entryName) {
      return range
        ? translations.entryWithRange(entryName, range)
        : translations.entryWithoutRange(entryName);
    }
    return range ? translations.rangeWithoutEntry(range) : '';
  },

  /** Summary of a stored query: the entry without a range waits for a date to be agreed. */
  buildSummary: (
    context: SummaryContext,
    translations: CalendarEntryQuerySummaryTranslations,
  ): string | undefined => {
    const { entry, range, type } = context;
    const resource = type && entry ? translations.typeWithEntry(type, entry) : (type ?? entry);
    const shownRange = range ?? (entry ? translations.withoutRange : '');
    return resource && shownRange
      ? translations.resourceWithRange(resource, shownRange)
      : [resource, shownRange].find(Boolean);
  },

  /**
   * Shows a form range, entered in the other booking mode, in the given one without changing its
   * meaning: goes through the request range, like a stored query. Full-day 12–14 becomes
   * 12 00:00–15 00:00; an exact range becomes the full days it touches.
   */
  changeBookingMode: (
    range: ProcessCalendarEntryQueryFormRange,
    bookingMode: BookingModeDto,
  ): ProcessCalendarEntryQueryFormRange => {
    const { requestedFrom: from, requestedTo: to } = range;
    const isExactTime = bookingMode === BookingModeDto.ExactTime;
    const shown = reservationRangeFromDto(
      bookingMode,
      from ? (isExactTime ? businessDay(inBusinessZone(from)) : from).toISOString() : undefined,
      to ? (isExactTime ? nextBusinessDay(inBusinessZone(to)) : to).toISOString() : undefined,
    );
    return { requestedFrom: shown.from, requestedTo: shown.to };
  },

  /**
   * Human readable range in the business zone: one day, one day with hours, or several days.
   * Takes the request range: a full-day end is exclusive (the last millisecond decides the last day).
   */
  formatRange: (from: Dayjs | string, to: Dayjs | string, bookingMode: BookingModeDto): string => {
    const range = reservationRangeFromDto(
      bookingMode,
      dayjs(from).format(INSTANT_FORMAT),
      dayjs(to).format(INSTANT_FORMAT),
    );
    const start = range.from!;
    const end = range.to!;
    if (bookingMode === BookingModeDto.ExactTime) {
      return start.isSame(end, 'day')
        ? `${start.format(DATE_FORMAT)} ${start.format(TIME_FORMAT)}–${end.format(TIME_FORMAT)}`
        : `${start.format(`${DATE_FORMAT} ${TIME_FORMAT}`)}–${end.format(`${DATE_FORMAT} ${TIME_FORMAT}`)}`;
    }
    const lastDay = end.isBefore(start) ? start : end;
    return start.isSame(lastDay, 'day')
      ? start.format(DATE_FORMAT)
      : `${start.format(DATE_FORMAT)}–${lastDay.format(DATE_FORMAT)}`;
  },

  fromValues: (
    values: ProcessCalendarEntryQueryValues,
    typeLabel: (type: DictionaryTypeDto) => string,
    bookingMode: BookingModeDto,
  ): ProcessCalendarEntryQueryFormModel => ({
    ...processCalendarEntryQueryUtil.readRange(values, bookingMode),
    requestedDictionaryId:
      values.requestedDictionaryId != null
        ? { label: values.requestedDictionaryName ?? '', value: values.requestedDictionaryId }
        : null,
    requestedDictionaryType: values.requestedDictionaryType
      ? {
          label: typeLabel(values.requestedDictionaryType),
          value: values.requestedDictionaryType,
        }
      : null,
  }),

  hasQuery: (process: ProcessCalendarEntryQueryValues): boolean =>
    QUERY_FIELDS.some(field => process[field] != null && process[field] !== ''),

  /** Only a range needs the booking mode: without one the query is shown and saved normally. */
  isBookingModeError: (isError: boolean, from: unknown, to: unknown): boolean =>
    Boolean(isError && from && to),

  /**
   * Types a query may name: a shared type is an add-on of the offer, so it is left out - except
   * the current type of a stored query, which stays shown. An unknown configuration does not hide a type.
   */
  queryableTypes: (
    types: DictionaryTypeDto[],
    configurationByType: Partial<Record<DictionaryTypeDto, DictionaryTypeCalendarConfigurationDto>>,
    current?: DictionaryTypeDto | null,
  ): DictionaryTypeDto[] =>
    types.filter(
      type =>
        type === current ||
        configurationByType[type]?.occupancyMode !== OccupancyModeDto.Shared,
    ),

  /** Form range of stored or prefilled values (ISO request range) in the given booking mode. */
  readRange: (
    values: ProcessCalendarEntryQueryIsoRange,
    bookingMode: BookingModeDto,
  ): ProcessCalendarEntryQueryFormRange => {
    const range = reservationRangeFromDto(bookingMode, values.requestedFrom, values.requestedTo);
    return { requestedFrom: range.from, requestedTo: range.to };
  },

  toRequest: (
    form: ProcessCalendarEntryQueryFormModel,
    bookingMode: BookingModeDto,
  ): ProcessCalendarEntryQueryDto => {
    const range =
      form.requestedFrom && form.requestedTo
        ? toRequestRange(form.requestedFrom, form.requestedTo, bookingMode)
        : undefined;
    return {
      requestedDictionaryId: form.requestedDictionaryType
        ? form.requestedDictionaryId?.value
        : undefined,
      requestedDictionaryType: form.requestedDictionaryType?.value,
      requestedFrom: range?.from.toISOString(),
      requestedTo: range?.to.toISOString(),
    };
  },

  withCurrentOption: (
    options: SelectSearchOptionModel<number>[],
    current: SelectSearchOptionModel<number> | null | undefined,
  ): SelectSearchOptionModel<number>[] =>
    current && current.label && !options.some(option => option.value === current.value)
      ? [...options, current]
      : options,
};
